import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import './Modal.css';

export const Modal = ({ isOpen, onClose, title, children }) => {
  // Handle Escape key press
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      >
        {/* Backdrop with enhanced blur and animation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-md"
        />

        {/* Modal Content */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 30 
          }}
          className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-white/20"
        >
          {/* Enhanced glass effect header */}
          <div className="bg-gradient-to-r from-blue-50/90 to-indigo-50/90 backdrop-blur-xl border-b border-white/20">
            <div className="flex items-center justify-between p-6">
              <motion.h3 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-2xl font-semibold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-transparent"
              >
                {title}
              </motion.h3>
              <motion.button
                whileHover={{ rotate: 90 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                onClick={onClose}
                className="p-2 hover:bg-black/5 rounded-full transition-colors duration-200 group"
              >
                <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              </motion.button>
            </div>
          </div>

          {/* Enhanced content with gradient and animations */}
          <div className="bg-gradient-to-b from-white/80 to-gray-50/80 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar"
            >
              <div className="space-y-6 relative">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent pointer-events-none" />
                {children}
              </div>
            </motion.div>
          </div>

          {/* Enhanced footer with animation */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="p-4 border-t border-white/20 bg-white/80 backdrop-blur-sm"
          >
            <p className="text-sm text-center text-gray-500 flex items-center justify-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 rounded-md text-xs">ESC</kbd>
              <span>or click outside to close</span>
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}; 